<script setup lang="ts">
import type { InteractionPreviewProps } from '../../../../model/assessments/preview-interactions.hook'

import { AppText } from '@shared/ui/typography'

import { usePreviewMultipleChoice } from '../../../../model/assessments/preview-interactions.hook'
import PreviewAuthorNote from '../PreviewAuthorNote.vue'

/**
 * The options, unanswered — radios for `single`, checkboxes for `multi`, disabled and without
 * `v-model`, since the preview writes nothing.
 *
 * The one authoring exception to "no grading": which option is `correct` is shown, but only inside
 * `PreviewAuthorNote`. It repeats what the author wrote; it never evaluates a response.
 */
defineOptions({
  name: 'PreviewMultipleChoice',
})

const props = defineProps<InteractionPreviewProps>()

const {
  groupName,
  refIsMulti,
  refModeLabel,
  refChoices,
  refHasChoices,
  refHasAnswerKey,
} = usePreviewMultipleChoice(() => props.interaction)
</script>

<template>
  <div class="preview-multiple-choice">
    <AppText
      name="body2"
      color="slate-500"
    >
      {{ refModeLabel }}
    </AppText>

    <!-- Zero options is a legitimate half-built state (the schema wants two, and validation
         says so on save). It renders as a statement, not as a crash. -->
    <AppText
      v-if="!refHasChoices"
      name="body2"
      color="slate-500"
    >
      No options yet — a kid would see nothing to choose from.
    </AppText>

    <ul
      v-else
      class="preview-multiple-choice__options"
    >
      <li
        v-for="choice in refChoices"
        :key="choice.key"
        class="preview-multiple-choice__option"
      >
        <input
          :type="refIsMulti ? 'checkbox' : 'radio'"
          :name="groupName"
          disabled
        >
        <AppText name="body2">
          {{ choice.text || `Option ${choice.position} has no text yet` }}
        </AppText>
        <PreviewAuthorNote v-if="choice.isCorrect">
          correct
        </PreviewAuthorNote>
      </li>
    </ul>

    <PreviewAuthorNote v-if="refHasChoices && !refHasAnswerKey">
      no option is marked correct yet
    </PreviewAuthorNote>
  </div>
</template>

<style scoped lang="scss" src="./PreviewMultipleChoice.scss" />
